import React, { useRef, useEffect, useState } from 'react';
import { Rect, Circle, Arrow, Line, Transformer, Group, Text } from 'react-konva';

export default function ShapeElement({ 
  element, 
  isSelected, 
  isBeingEdited,
  onSelect, 
  onDragEnd, 
  onTransformEnd,
  onUpdate,
  canEdit 
}) {
  const shapeRef = useRef(null);
  const transformerRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const props = element.properties || {};
  const fill = props.fill || 'transparent';
  const stroke = props.stroke || '#000000';
  const strokeWidth = props.strokeWidth || 2;
  
  // Text properties for rectangles
  const tileText = props.text || '';
  const textFontSize = props.fontSize || 16;
  const textColor = props.textColor || '#000000';
  
  // Attach transformer when selected
  useEffect(() => {
    if (isSelected && transformerRef.current && shapeRef.current) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  // Handle double-click to edit text
  const handleDoubleClick = (e) => {
    if (!canEdit || element.type !== 'shape_rect') return;
    
    e.cancelBubble = true;
    setIsEditing(true);
    
    const stage = e.target.getStage();
    const stageBox = stage.container().getBoundingClientRect();
    const position = shapeRef.current.getAbsolutePosition();
    const scale = stage.scaleX();
    
    // Create textarea for editing
    const textarea = document.createElement('textarea');
    textarea.value = tileText;
    textarea.style.position = 'absolute';
    textarea.style.left = `${stageBox.left + position.x * scale}px`;
    textarea.style.top = `${stageBox.top + position.y * scale}px`;
    textarea.style.width = `${(element.width || 150) * scale}px`;
    textarea.style.height = `${(element.height || 100) * scale}px`;
    textarea.style.fontSize = `${textFontSize * scale}px`;
    textarea.style.fontFamily = 'Inter, system-ui, sans-serif';
    textarea.style.color = textColor;
    textarea.style.background = 'transparent';
    textarea.style.border = '2px solid #3B82F6';
    textarea.style.borderRadius = '4px';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.padding = '8px';
    textarea.style.textAlign = 'center';
    textarea.style.zIndex = '1000';
    textarea.placeholder = 'Enter tile title...';
    
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    
    const handleBlur = () => {
      onUpdate(element.id, {
        properties: { ...props, text: textarea.value }
      });
      document.body.removeChild(textarea);
      setIsEditing(false);
    };
    
    textarea.addEventListener('blur', handleBlur);
    textarea.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape') {
        document.body.removeChild(textarea);
        setIsEditing(false);
      }
      if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        textarea.blur();
      }
    });
  };

  const commonProps = {
    ref: shapeRef,
    x: element.x,
    y: element.y,
    rotation: element.rotation || 0,
    scaleX: element.scale_x || 1,
    scaleY: element.scale_y || 1,
    draggable: canEdit,
    onClick: (e) => onSelect(element.id, e),
    onTap: (e) => onSelect(element.id, e),
    onDblClick: handleDoubleClick,
    onDblTap: handleDoubleClick,
    onDragStart: (e) => {
      e.cancelBubble = true;
    },
    onDragEnd: (e) => {
      e.cancelBubble = true;
      onDragEnd(element.id, { x: e.target.x(), y: e.target.y() });
    },
    onTransformEnd: () => {
      const node = shapeRef.current;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      
      node.scaleX(1);
      node.scaleY(1);
      
      if (element.type === 'shape_arrow' || element.type === 'connector' || element.type === 'connector_vertical' || element.type === 'connector_drawn') {
        // For arrows/connectors, scale the points
        const points = props.points || [0, 0, 150, 0];
        const scaledPoints = points.map((p, i) => i % 2 === 0 ? p * scaleX : p * scaleY);
        
        onTransformEnd(element.id, {
          x: node.x(),
          y: node.y(),
          rotation: node.rotation(),
          properties: { ...props, points: scaledPoints }
        });
      } else {
        onTransformEnd(element.id, {
          x: node.x(),
          y: node.y(),
          width: Math.max(20, (element.width || 100) * scaleX),
          height: Math.max(20, (element.height || 100) * scaleY),
          rotation: node.rotation()
        });
      }
    }
  };

  const renderShape = () => {
    const selectionStroke = isSelected ? '#3B82F6' : (isBeingEdited ? isBeingEdited.color : stroke);
    const width = element.width || 150;
    const height = element.height || 100;
    
    switch (element.type) {
      case 'shape_rect':
        return (
          <Group {...commonProps}>
            <Rect
              width={width}
              height={height}
              fill={fill}
              stroke={selectionStroke}
              strokeWidth={strokeWidth}
              cornerRadius={props.cornerRadius || 4}
            />
            {/* Tile text centered in rectangle */}
            {tileText && !isEditing && (
              <Text
                text={tileText}
                x={0}
                y={0}
                width={width}
                height={height}
                fontSize={textFontSize}
                fontFamily="Inter, system-ui, sans-serif"
                fill={textColor}
                align="center"
                verticalAlign="middle"
                padding={8}
                listening={false}
              />
            )}
            {/* Placeholder text when empty */}
            {!tileText && !isEditing && (
              <Text
                text="Double-click to add title"
                x={0}
                y={0}
                width={width}
                height={height}
                fontSize={12}
                fontFamily="Inter, system-ui, sans-serif"
                fill="#9CA3AF"
                align="center"
                verticalAlign="middle"
                padding={8}
                listening={false}
              />
            )}
          </Group>
        );
      
      case 'shape_circle':
        return (
          <Circle
            {...commonProps}
            x={element.x + (element.width || 100) / 2}
            y={element.y + (element.height || 100) / 2}
            radius={Math.min(element.width || 100, element.height || 100) / 2}
            fill={fill}
            stroke={selectionStroke}
            strokeWidth={strokeWidth}
            onDragEnd={(e) => {
              onDragEnd(element.id, { 
                x: e.target.x() - (element.width || 100) / 2, 
                y: e.target.y() - (element.height || 100) / 2 
              });
            }}
          />
        );
      
      case 'shape_arrow':
        return (
          <Arrow
            {...commonProps}
            points={props.points || [0, 0, 150, 0]}
            stroke={selectionStroke}
            strokeWidth={strokeWidth}
            fill={selectionStroke}
            pointerLength={props.pointerLength || 10}
            pointerWidth={props.pointerWidth || 10}
            lineCap="round"
            lineJoin="round"
            hitStrokeWidth={Math.max(20, strokeWidth + 16)}
          />
        );
      
      case 'connector':
      case 'connector_vertical':
      case 'connector_drawn':
        // Connector with arrows on both ends
        const points = props.points || (element.type === 'connector_vertical' ? [0, 0, 0, 150] : [0, 0, 200, 0]);
        const pointerLength = props.pointerLength || 10;
        const pointerWidth = props.pointerWidth || 10;
        const tension = props.tension || 0;
        
        // Create click handler that's shared by all connector parts
        const connectorClickProps = {
          onClick: (e) => onSelect(element.id, e),
          onTap: (e) => onSelect(element.id, e),
        };
        
        return (
          <Group {...commonProps}>
            {/* Main line with large hit area */}
            <Line
              points={points}
              stroke={selectionStroke}
              strokeWidth={strokeWidth}
              lineCap="round"
              lineJoin="round"
              tension={tension}
              hitStrokeWidth={Math.max(25, strokeWidth + 20)}
              {...connectorClickProps}
            />
            {/* End arrow */}
            <Arrow
              points={points.length >= 4 
                ? [points[points.length - 4] || 0, points[points.length - 3] || 0, points[points.length - 2], points[points.length - 1]]
                : [0, 0, points[points.length - 2], points[points.length - 1]]
              }
              stroke={selectionStroke}
              strokeWidth={strokeWidth}
              fill={selectionStroke}
              pointerLength={pointerLength}
              pointerWidth={pointerWidth}
              {...connectorClickProps}
            />
            {/* Start arrow (reverse direction) */}
            <Arrow
              points={[points[2] || points[0], points[3] || points[1], points[0], points[1]]}
              stroke={selectionStroke}
              strokeWidth={strokeWidth}
              fill={selectionStroke}
              pointerLength={pointerLength}
              pointerWidth={pointerWidth}
              {...connectorClickProps}
            />
          </Group>
        );
      
      default:
        return null;
    }
  };

  const getTransformerAnchors = () => {
    // For arrows and connectors, show anchors at both ends for length adjustment
    if (element.type === 'shape_arrow') {
      return ['middle-left', 'middle-right'];
    }
    if (element.type === 'connector') {
      return ['middle-left', 'middle-right'];
    }
    if (element.type === 'connector_vertical') {
      return ['top-center', 'bottom-center'];
    }
    if (element.type === 'connector_drawn') {
      return ['middle-left', 'middle-right', 'top-center', 'bottom-center'];
    }
    if (element.type === 'shape_circle') {
      return ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
    }
    return ['top-left', 'top-center', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right'];
  };

  return (
    <>
      {renderShape()}
      
      {isSelected && canEdit && (
        <Transformer
          ref={transformerRef}
          enabledAnchors={getTransformerAnchors()}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 20 || newBox.height < 20) {
              return oldBox;
            }
            return newBox;
          }}
          rotateEnabled={true}
          rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
          rotateAnchorOffset={20}
          keepRatio={element.type === 'shape_circle'}
          anchorSize={10}
          anchorCornerRadius={5}
          borderStroke="#3B82F6"
          anchorStroke="#3B82F6"
          anchorFill="#FFFFFF"
        />
      )}
    </>
  );
}
